import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Investment } from '@/components/dealCatalog-page/types';
import { allStrategiesTableColumns } from '@/components/managerUpdates-page/constants';
import { isMoreThan90DaysInPast } from '@/components/managerUpdates-page/utils';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { dateFormatter } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import styled from 'styled-components';
import { ManagerUpdatesPageListInvestmentsForManagerUpdateQuery } from '@/__generated__/graphql';

type MobileTableProps = {
  tableData: ManagerUpdatesPageListInvestmentsForManagerUpdateQuery['listInvestmentsForManagerUpdate']['data'];
  openManagerUpdateDetailsPage: ({ strategyId, documentRecordId }: { strategyId: string; documentRecordId?: string }) => void;
};

const tooltipContent = (el: ManagerUpdatesPageListInvestmentsForManagerUpdateQuery['listInvestmentsForManagerUpdate']['data'][number]) => {
  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TextCell>Draft: {el.documentRecordMetadata?.num_of_mu_draft}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Published: {el.documentRecordMetadata?.num_of_mu_published}</TextCell>
      </TableRowCell>
    </TableBodyWrapper>
  );
};

const MobileTable = ({ tableData, openManagerUpdateDetailsPage }: MobileTableProps) => {
  if (!tableData.length) {
    return <NoResults />;
  }

  return (
    <MobileTableWrapper>
      {tableData.map((el) => {
        return (
          <MobileTableRowWrap key={el.id}>
            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[0].title}</MobileRowTitle>
              <MobileRowValue onClick={() => openManagerUpdateDetailsPage({ strategyId: el.id })} isRedirect>
                {el.name}
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[1].title}</MobileRowTitle>
              <MobileRowValue>{el.managerId}</MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[2].title}</MobileRowTitle>
              <MobileRowValue>{el.securityStatus}</MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[3].title}</MobileRowTitle>
              <MobileRowValue>
                <CellWithTooltip>
                  {el?.documentRecordMetadata?.num_of_mu}
                  <Tooltip title={tooltipContent(el)}>
                    <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                  </Tooltip>
                </CellWithTooltip>
              </MobileRowValue>
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[4].title}</MobileRowTitle>

              {el?.documentRecordMetadata?.new_ai_suggestions_date ? (
                <MobileRowValue
                  onClick={() =>
                    openManagerUpdateDetailsPage({ strategyId: el.id, documentRecordId: el?.documentRecordMetadata?.new_ai_suggestions_document_id })
                  }
                  isRedirect
                >
                  {dateFormatter(format(new Date(el.documentRecordMetadata.new_ai_suggestions_date), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
                </MobileRowValue>
              ) : (
                <MobileRowValue>--</MobileRowValue>
              )}
            </MobileRow>

            <MobileRow>
              <MobileRowTitle>{allStrategiesTableColumns[5].title}</MobileRowTitle>
              <LastPublishDateMobileRowValue isMoreThan90DaysInPast={isMoreThan90DaysInPast(el?.documentRecordMetadata?.last_mu_publish_date)}>
                {el?.documentRecordMetadata?.last_mu_publish_date &&
                  dateFormatter(format(new Date(el.documentRecordMetadata.last_mu_publish_date), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
              </LastPublishDateMobileRowValue>
            </MobileRow>
          </MobileTableRowWrap>
        );
      })}
    </MobileTableWrapper>
  );
};

export default MobileTable;

const LastPublishDateMobileRowValue = styled(MobileRowValue)<{ isMoreThan90DaysInPast: boolean }>`
  font-weight: 600;
  color: ${({ isMoreThan90DaysInPast }) => (isMoreThan90DaysInPast ? themeVar('context.error') : themeVar('font.base'))};
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
